import { Link } from 'react-router-dom';
import { RotateCcw, Calendar, Layers, Target, CheckSquare, BarChart3 } from 'lucide-react';
import { AreaDocLayout, DocDivider, ReferenceTable } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { ConceptCard } from '../components/ConceptCard';
import { StepGuide } from '../components/StepGuide';
import { CycleAnatomyDiagram } from '../components/diagrams/CycleAnatomyDiagram';
import { useDiagramTheme } from '../components/diagrams/useDiagramTheme';

function CyclePhaseDiagram() {
  const t = useDiagramTheme();
  const phases = [
    { label: 'Design', sub: 'Configure scope, tracks, participants', bg: t.indigoBg, border: t.indigoBorder, text: t.indigoText },
    { label: 'Readiness', sub: 'Sessions: pool, criteria, values', bg: t.violetBg, border: t.violetBorder, text: t.violetText },
    { label: 'Ranking', sub: 'Force-ranking sessions per track', bg: t.emeraldBg, border: t.emeraldBorder, text: t.emeraldText },
    { label: 'Governance', sub: 'Catchball loops, proposals, ratify', bg: t.amberBg, border: t.amberBorder, text: t.amberText },
    { label: 'Publication', sub: 'Published priority lists', bg: t.greenBg, border: t.greenBorder, text: t.greenText },
  ];

  return (
    <div className="w-full border p-5 overflow-x-auto transition-colors" style={{ background: t.canvasBg, borderColor: t.canvasBorder }}>
      <svg viewBox="0 0 720 100" className="w-full" style={{ minWidth: 540 }}>
        <defs>
          <marker id="cp-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill={t.arrowColor} />
          </marker>
        </defs>
        {phases.map((p, i) => {
          const x = 10 + i * 143;
          return (
            <g key={p.label}>
              <rect x={x} y="18" width="127" height="62" rx="8" fill={p.bg} stroke={p.border} strokeWidth="1.5" />
              <text x={x + 63} y="40" textAnchor="middle" fontSize="11" fontWeight="700" fill={p.text} fontFamily="sans-serif">{p.label}</text>
              <text x={x + 63} y="56" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">{p.sub.split(',')[0]}</text>
              <text x={x + 63} y="68" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">{p.sub.split(',').slice(1).join(',').trim()}</text>
              {i < phases.length - 1 && (
                <line x1={x + 129} y1="49" x2={x + 141} y2="49" stroke={t.arrowColor} strokeWidth="1.5" markerEnd="url(#cp-arrow)" />
              )}
            </g>
          );
        })}
      </svg>
      <p className="text-center text-xs mt-1" style={{ color: t.canvasText }}>The five phases of a prioritization cycle — each phase can overlap with the next</p>
    </div>
  );
}

export default function CyclesDocs() {
  return (
    <AreaDocLayout
      icon={RotateCcw}
      iconBg="bg-[var(--slate-dim)] border-[var(--slate)]"
      iconColor="text-[var(--text-ter)]"
      title="Cycle Design & Execution"
      subtitle="The governance container for a prioritization effort — from scope definition through published priority lists."
      tag="Governed"
      tagColor="bg-[var(--slate-dim)] text-[var(--text-sec)]"
    >
      {/* Overview */}
      <DocSection
        title="Overview"
        description="A cycle defines the scope, timing, and authority structure for a complete prioritization effort."
        accent="blue"
      >
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 text-sm text-[var(--text-sec)] leading-relaxed">
          A <strong>Cycle</strong> is the highest-level governance container. It defines what is being prioritized (scope), who participates, how authority flows (via <Link to="/prioritization/documentation/catchball" className="underline text-[var(--text-sec)]">Catchball</Link>), and when the effort concludes.
          A cycle does not execute ranking — it governs the tracks and sessions that do.
          Every cycle produces one or more <strong>Published Priority Lists</strong> — the formal output of the prioritization effort.
        </div>
      </DocSection>

      {/* Phase timeline */}
      <DocSection
        title="Cycle Phases"
        description="Cycles follow the DEGAP process framework from Managing Priorities by Harry Max. Five phases, each governed by session work and authority events."
        accent="blue"
      >
        <CyclePhaseDiagram />
        {/* Moved here 2026-08-27 from the banner that sat above the Cycles list.
            A framework explanation is reference material — it belongs where you
            go to learn, not pinned above the list you use every day. */}
        <div className="mt-5 space-y-3">
          {[
            { label: 'Decide', text: 'Determine if the upside of intentional prioritization outweighs the cost of letting priorities emerge on their own.' },
            { label: 'Engage', text: 'Carve out necessary time and space, identify sources of information, and pinpoint your stakeholders.' },
            { label: 'Gather', text: 'Investigate what matters and why — solicit input, dig up potential priorities, and uncover key metadata.' },
            { label: 'Arrange', text: 'Review, clean up, and normalize items — clarify what each is, remove ambiguity, and begin quantifying value.' },
            { label: 'Prioritize', text: 'Arrange refined items in order of importance using the method best suited to your needs and constraints.' },
          ].map((phase, i) => (
            <div key={phase.label} className="flex gap-3">
              <span className="flex-shrink-0 mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--slate-dim)] text-[11px] font-semibold text-[var(--text-sec)] dark:bg-[var(--slate)]/50">
                {i + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{phase.label}</div>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">{phase.text}</p>
              </div>
            </div>
          ))}
        </div>
      </DocSection>

      {/* Anatomy */}
      <DocSection
        title="Cycle Anatomy"
        description="Inside a cycle: Tracks own sessions and results; catchball loops govern authority transitions."
        accent="blue"
      >
        <CycleAnatomyDiagram />
      </DocSection>

      <DocDivider />

      {/* Key concepts */}
      <DocSection title="Key Concepts" accent="blue">
        <div className="grid gap-3 md:grid-cols-2">
          <ConceptCard
            term="Scope"
            definition="The organizational boundary for a cycle — which teams, areas, or work types are included. Scope constrains which items are eligible."
            icon={Target}
            color="blue"
          />
          <ConceptCard
            term="Track"
            definition="A Comparison Cell (Category × Altitude) activated within this specific cycle and scope. Each Track owns its own sessions and results."
            icon={Layers}
            color="blue"
            href="/prioritization/documentation/tracks"
          />
          <ConceptCard
            term="Cycle Readiness"
            definition="The formal configuration phase before ranking begins: participant lists, item pools, criteria, scoring scales, and value completion are all cycle-readiness concerns."
            icon={CheckSquare}
            color="blue"
            tag="Pre-ranking"
          />
          <ConceptCard
            term="Published Priority List"
            definition="The ratified output of a cycle. Once ratified and published, the priority list is the organization's priorities and is what planning builds a plan from."
            icon={BarChart3}
            color="blue"
          />
          <ConceptCard
            term="Catchball Loop"
            definition="One complete authority iteration within a cycle: request flows down, teams work, proposals flow up, authority accepts or requests revision."
            icon={RotateCcw}
            color="blue"
            href="/prioritization/documentation/catchball"
          />
          <ConceptCard
            term="Cycle Timeline"
            definition="The defined start/end window for the cycle, which constrains session scheduling, loop deadlines, and publication dates."
            icon={Calendar}
            color="blue"
          />
        </div>
      </DocSection>

      <DocDivider />

      {/* Common tasks */}
      <DocSection title="Common Tasks" accent="blue">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <p className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-3">Design a New Cycle</p>
            <StepGuide color="blue" steps={[
              { title: 'Go to Cycles → New Cycle', description: 'Open cycle design from the Cycles section in the left nav.' },
              { title: 'Define scope and timeline', description: 'Set which areas and teams are in scope, and the start/end window for the cycle.' },
              { title: 'Add Tracks', description: 'Select the (Category × Altitude) combinations that will be prioritized. Each becomes a Track with its own session workspace.' },
              { title: 'Configure participants', description: 'Assign facilitators, raters, and governance roles for each track. This is a configuration session (type: participants).' },
              { title: 'Launch', description: 'Publish the cycle design to make it available to participants.' },
            ]} />
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <p className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-3">Run a Cycle to Completion</p>
            <StepGuide color="blue" steps={[
              { title: 'Complete readiness sessions', description: 'Run item pool, criteria, scales, and values sessions to configure the comparison environment for each track.' },
              { title: 'Run ranking sessions', description: 'Execute force-ranking sessions per track to produce immutable ranked results.' },
              { title: 'Form proposals', description: 'Per track, prepare a proposal document that packages the ranked result with rationale and tradeoffs.' },
              { title: 'Start catchball', description: 'Initiate the first loop: authority tier sends a request, teams submit proposals. See Catchball docs.', note: 'Loops may repeat if revision is requested.' },
              { title: 'Ratify and publish', description: 'Authority tier accepts proposals → cycle ratification → published priority list.' },
            ]} />
          </div>
        </div>
      </DocSection>

      <DocDivider />

      {/* Reference */}
      <DocSection title="Cycle Field Reference" accent="blue">
        <ReferenceTable rows={[
          { field: 'id', type: 'uuid', description: 'Unique cycle identifier.' },
          { field: 'name', type: 'string', description: 'Display name of the cycle.' },
          { field: 'scope', type: 'object', description: 'Scope definition: areas, teams, or other boundary criteria.' },
          { field: 'status', type: 'enum', description: 'Cycle state: design, active, completed, archived.' },
          { field: 'start_date', type: 'date?', description: 'When the cycle work begins.' },
          { field: 'end_date', type: 'date?', description: 'Target completion date for the cycle.' },
          { field: 'workspace_id', type: 'uuid', description: 'Workspace this cycle belongs to.' },
          { field: 'created_at', type: 'timestamp', description: 'When the cycle was created.' },
        ]} />
      </DocSection>
    </AreaDocLayout>
  );
}
